/**
 * The boss's bolts and stun (02 §9.4), on M-ticks: the main loop's slot at $0A38 runs
 * `special_item_handler` ($1134, mislabelled) on level 100 (set up at $2C7A). While the boss fights,
 * each lightning bolt (type $26) whose cell lies within -8..63 px across and -8..55 px down of the
 * boss is used up ($3A) and takes one off D_5BBF; once D_5BBF drops below 0 the boss is stunned ($BE =
 * 9, D_5BBF = 130). Stunned, D_5BBF counts the passes down ($118F), the boss's sprites toggling on
 * every even count below $11 ($11A3); at 0 it recovers angry (D_872A) with D_5BBF = $28 ($1194).
 *
 * D_5BBF starts at $32 (50), so it takes 51 bolts, and 41 after a stun: `Boss.hp` is the bolts left.
 */

import { inReach } from './move';
import { BOSS } from '../tuning';
import type { World } from '../../types';

/**
 * A bolt at (x, y), its 16x16 top-left in the VIC frame (8col + $14, 8row + $15): if the boss is
 * fighting and the bolt is in its box, the boss loses a bolt's worth (and the bolt is used up: the
 * caller stops it). True if it hit.
 */
export function boltHitsBoss(world: World, x: number, y: number): boolean {
  const b = world.boss;
  if (!b || b.state !== 'fight' || b.ai.wake > 0) return false;
  if (!inReach(x - b.x, BOSS.reachX) || !inReach(y - b.y, BOSS.reachY)) return false;
  b.hp = Math.max(0, b.hp - 1);
  world.events.push({ type: 'bossHit', hp: b.hp });
  if (b.hp === 0) {
    b.state = 'stunned';
    b.ai.stun = BOSS.stun;
    b.ai.stunPass = world.clock.mTicks;
    world.events.push({ type: 'bossStunned' });
  }
  return true;
}

/**
 * $11A3's blink: the sprites toggle at counts 16, 14 ... 2, starting from shown - hidden at 16, 15,
 * 12, 11, 8, 7, 4, 3.
 */
const blinkOff = (count: number): boolean => count <= BOSS.blinkFrom && ((BOSS.blinkFrom - count) & 2) === 0;

/** $118F: one pass of the stun (not the pass it began in). At 0 the boss recovers, angry. */
export function bossMTick(world: World): void {
  const b = world.boss;
  if (!b || b.state !== 'stunned' || b.ai.stunPass === world.clock.mTicks) return;
  const a = b.ai;
  a.stun--;
  if (a.stun > 0) {
    b.blink = blinkOff(a.stun);
    return;
  }
  b.state = 'fight';
  b.blink = false;
  b.angry = true;
  b.hp = BOSS.recoverHits;
  world.events.push({ type: 'bossRecovered' });
}
